/**
 * ONE PAGE OF THE LIBRARY, FROM THE DATABASE.
 *
 * A page is `size` rows after a cursor, in the order asked for. One more row
 * than the page is requested, never shown: its presence is how "Next" knows
 * there is a next, without a second query and without counting.
 *
 * NEVER MORE THAN A PAGE. The largest list request this can make is 101
 * documents (a page of 100 and the one beyond it), and at most one more read
 * per row shown, for the ownership record or the policy beside it. There is
 * no code path here that reads a library.
 *
 * The total ("1–50 of 4,782") is a count aggregation over the same query: a
 * number, without the documents.
 */
import {
  collection, doc, documentId, getCountFromServer, getDoc, getDocs, getFirestore, limit, orderBy, query, startAfter, Timestamp, where,
  type DocumentData, type Query, type QueryConstraint, type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { app } from '../../../lib/firebase';
import { calendarDate } from '../../../shared/dates';
import { POLICY_STATUSES, type PolicyStatus } from '../../../types/policy';
import type { PageSize, QueryShape } from './libraryQuery';

const db = getFirestore(app);

/** Mirrors NO_REVIEW in functions/src/policies/listFields.ts: a policy with no review date. */
export const NO_REVIEW = 9_999_999_999_999;

export type OwnerState = 'active' | 'inactive' | 'unassigned';

/** A row of the library. */
export interface LibraryRow {
  id: string;
  policyNumber: string;
  title: string;
  status: PolicyStatus;
  revisionStatus: 'draft' | 'in_review' | 'approved' | null;
  departmentNames: string[];
  categoryName: string | null;
  documentTypeName: string | null;
  /** "YYYY-MM-DD", or null when no review date is set. */
  reviewDate: string | null;
  /** Where the words were found, when this row answers a search: marked-up text and what it is from. */
  match?: { title: string | null; snippet: string | null; source: 'summary' | 'content' | 'attachment' | 'none' };
  /**
   * Who owns it. NULL FOR A READER, always: the three below come from the
   * ownership record, which is only asked for on behalf of people who work on
   * policies and which the database refuses to anybody else. Null also where
   * there is no record: a retired policy, or one saved a moment ago.
   */
  owner: {
    name: string | null;
    state: OwnerState;
    /** The address an import named, for a policy still waiting for that person to join. */
    waitingFor: string | null;
  } | null;
}

/** list.reviewSort as "YYYY-MM-DD"; null for the no-date value or anything that is not one. */
export function reviewDayOf(v: unknown): string | null {
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0 || v >= NO_REVIEW) return null;
  return new Date(v).toISOString().slice(0, 10);
}

const text = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const texts = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : []);

export function toOwner(d: DocumentData | undefined): LibraryRow['owner'] {
  if (!d) return null;
  const state = d.ownerState === 'active' || d.ownerState === 'inactive' ? d.ownerState : 'unassigned';
  return {
    name: text(d.ownerName),
    state,
    // Whenever an import is still waiting for the person its sheet named,
    // whoever holds the policy meanwhile. The handover clears it.
    waitingFor: text(d.pendingOwnerEmail),
  };
}

/** A row from a policy document. Nothing about its owner is read from here. */
export function toLibraryRow(id: string, d: DocumentData): LibraryRow {
  const list = (d.list && typeof d.list === 'object' ? d.list : {}) as Record<string, unknown>;
  const status = (POLICY_STATUSES as readonly string[]).includes(d.status) ? (d.status as PolicyStatus) : 'draft';
  const revision = d.revisionStatus;
  return {
    id,
    policyNumber: text(d.policyNumber) ?? '',
    title: text(d.title) ?? '(untitled)',
    status,
    revisionStatus: revision === 'draft' || revision === 'in_review' || revision === 'approved' ? revision : null,
    departmentNames: texts(list.departmentNames),
    categoryName: text(list.categoryName),
    documentTypeName: text(list.documentTypeName),
    // From the value the list sorts and filters on, so a row never disagrees
    // with the order or the view it is in.
    reviewDate: reviewDayOf(list.reviewSort) ?? calendarDate(d.nextReviewAt ?? d.nextReviewDate),
    owner: null,
  };
}

/** The shape as database constraints, without a limit or a cursor. */
export function constraintsFor(shape: QueryShape): QueryConstraint[] {
  return [
    ...shape.where.map(([field, op, value]) => where(field, op, value)),
    ...shape.orderBy.map(([field, dir]) => orderBy(field === '__name__' ? documentId() : field, dir)),
  ];
}

const under = (orgId: string, name: QueryShape['collection']) => collection(db, 'orgs', orgId, name);

/**
 * Where a page ends, as plain values: the last row's sort value and its id.
 *
 * Values and not the database's own document handle, so that it can be kept
 * in the browser's history entry: Back, Forward and Refresh then return to
 * the same page of the same list, and the list beside an open policy can ask
 * for the page it was opened from. It is the position the query's own
 * `orderBy` defines, so it is exact, and it never goes in the address.
 */
export interface Cursor {
  /** The value of the field the list is ordered by. A time is kept as seconds and nanoseconds. */
  v: string | number | { s: number; n: number } | null;
  id: string;
}

export function cursorOf(snap: QueryDocumentSnapshot, shape: QueryShape): Cursor {
  const raw: unknown = snap.get(shape.orderBy[0]![0]);
  const v = raw instanceof Timestamp ? { s: raw.seconds, n: raw.nanoseconds }
    : typeof raw === 'string' || typeof raw === 'number' ? raw : null;
  return { v, id: snap.id };
}

const cursorValue = (c: Cursor) => (c.v !== null && typeof c.v === 'object' ? new Timestamp(c.v.s, c.v.n) : c.v);

export interface LibraryPage {
  rows: LibraryRow[];
  /** Pass to the next request to get the page after this one. Null on the last page. */
  next: Cursor | null;
}

/** The most ids one "is one of" query takes. */
const IN_MAX = 30;

/** The ownership records for a page of policies: a handful of queries, never one per row. */
export async function ownersFor(orgId: string, ids: string[]): Promise<Map<string, LibraryRow['owner']>> {
  const found = new Map<string, LibraryRow['owner']>();
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += IN_MAX) chunks.push(ids.slice(i, i + IN_MAX));
  const snaps = await Promise.all(chunks.map((chunk) =>
    getDocs(query(under(orgId, 'policyOwnership'), where(documentId(), 'in', chunk), limit(chunk.length))).catch(() => null)));
  // A chunk that could not be read leaves its rows without an owner. The
  // list itself is never failed for want of its Owner column.
  for (const snap of snaps) for (const doc of snap?.docs ?? []) found.set(doc.id, toOwner(doc.data()));
  return found;
}

/**
 * One page.
 *
 * `withOwners` is whether the person asking is shown ownership at all
 * (seesOwners). False, the ownership records are never requested, so a
 * reader's page carries no owner because none was fetched, not because a
 * column was left undrawn.
 */
export async function fetchLibraryPage(
  orgId: string,
  shape: QueryShape,
  size: PageSize,
  after: Cursor | null,
  withOwners: boolean,
): Promise<LibraryPage> {
  const q: Query = query(
    under(orgId, shape.collection),
    ...constraintsFor(shape),
    ...(after ? [startAfter(cursorValue(after), after.id)] : []),
    limit(size + 1),
  );
  const snap = await getDocs(q);
  const shown = snap.docs.slice(0, size);
  const next = snap.docs.length > size ? cursorOf(shown[shown.length - 1]!, shape) : null;

  if (shape.collection === 'policyOwnership') {
    // An owner queue: the page IS ownership records, in order. Each one's
    // policy is read by id for the rest of its row; one no longer there, or
    // not this person's to read, is left out.
    const policies = await Promise.all(shown.map((doc) =>
      getDoc(doc_(orgId, doc.id)).catch(() => null)));
    const rows: LibraryRow[] = [];
    shown.forEach((own, i) => {
      const policy = policies[i];
      if (policy?.exists()) rows.push({ ...toLibraryRow(policy.id, policy.data()), owner: toOwner(own.data()) });
    });
    return { rows, next };
  }

  const rows = shown.map((doc) => toLibraryRow(doc.id, doc.data()));
  if (withOwners && rows.length > 0) {
    const owners = await ownersFor(orgId, rows.map((r) => r.id));
    // No record: a policy not in play has none, and one saved a moment ago
    // has none yet. NOT shown as "not assigned": that is a claim, and with no
    // record there is nothing to base it on.
    for (const row of rows) row.owner = owners.get(row.id) ?? null;
  }
  return { rows, next };
}

/** The most policies a named set holds: what one bulk upload can make. */
export const NAMED_SET_MAX = 100;

/**
 * A named handful of policies, by id: "the twelve I just uploaded". Read one
 * by one, because the ids are all there is to ask by; one that is gone or not
 * this person's to read is left out.
 */
export async function fetchPoliciesByIds(orgId: string, ids: readonly string[], withOwners: boolean): Promise<LibraryRow[]> {
  const wanted = ids.slice(0, NAMED_SET_MAX);
  const snaps = await Promise.all(wanted.map((id) => getDoc(doc_(orgId, id)).catch(() => null)));
  const rows = snaps.flatMap((snap) => (snap?.exists() ? [toLibraryRow(snap.id, snap.data())] : []));
  if (withOwners && rows.length > 0) {
    const owners = await ownersFor(orgId, rows.map((r) => r.id));
    for (const row of rows) row.owner = owners.get(row.id) ?? null;
  }
  return rows.sort((a, b) => a.title.localeCompare(b.title));
}

const doc_ = (orgId: string, policyId: string) => doc(db, 'orgs', orgId, 'policies', policyId);

/** How many policies the query matches, without reading any of them. */
export async function countLibrary(orgId: string, shape: QueryShape): Promise<number> {
  // An order is not needed to count, and leaving it out lets more indexes serve it.
  const q = query(under(orgId, shape.collection), ...shape.where.map(([field, op, value]) => where(field, op, value)));
  return (await getCountFromServer(q)).data().count;
}

/** How many former members the filter offers. A hospital's leavers over years, not its staff. */
const FORMER_MAX = 300;

/** Members who are no longer active, by name. An administrator's read. */
export async function fetchFormerMembers(orgId: string): Promise<{ uid: string; name: string }[]> {
  const snap = await getDocs(query(collection(db, 'orgs', orgId, 'members'), where('status', '!=', 'active'), limit(FORMER_MAX)));
  return snap.docs
    .map((d) => ({ uid: d.id, name: text(d.get('displayName')) ?? 'Unnamed member' }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
